import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import dayjs from 'dayjs';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';

import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SpinnerService } from '@app/shared/spinner.service';
import { ISO_DATE, pickedDateStr, todayStr } from '@app/shared/time';
import { Station, Store } from '@app/shared/_models/common';
import { csvFileName, downloadCsv } from '../reglement.csv';
import { SAFE_RESERVED_HINT, refusalText, signedMoney, sumCents } from '../reglement.models';
import { ReglementService } from '../reglement.service';
import {
	CashGaps, DayRow, EnvelopeRow, GAP_HINTS, GapPerson, GapStatus, GapTotals, LazyPeek, NightRow, PEEK_COLUMNS, Peek, StationOption, StrongboxCountRow,
	cashGapsCsv, dayLabel, dayPeople, dayRow, envelopeRow, envelopesCsv, gapPeople, gapTotals, nameOf, nightPeople, nightRow, personLabel, stationLabel, stationOptions,
	storeLabel, strongboxCountRow, strongboxCountsCsv, strongboxLabel,
} from './cash-gaps.models';

const PEEK_ID = 'gaps-peek';
// Montant focalisé, fenêtre ouverte : ces touches font défiler sa liste plutôt que la page (le volet est au bout du document, hors de portée de Tab).
const PEEK_SCROLL: Record<string, (b: HTMLElement) => number> = {
	ArrowDown: (b) => b.scrollTop + 40, ArrowUp: (b) => b.scrollTop - 40,
	PageDown: (b) => b.scrollTop + b.clientHeight - 40, PageUp: (b) => b.scrollTop - b.clientHeight + 40,
	Home: () => 0, End: (b) => b.scrollHeight,
};

// Carte de l'onglet Règlement, montée pour l'habilitation Responsable RH seule ; le serveur refuse les autres (403).
@Component({
	selector: 'app-cash-gaps',
	standalone: true,
	templateUrl: './cash-gaps.component.html',
	styleUrls: ['./cash-gaps.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [SelectDateComponent, FormsModule, NgTemplateOutlet, MatButtonModule, MatCheckboxModule, MatIconModule, MatTooltipModule, FormatCurrencyPipe],
})
export class CashGapsComponent implements OnChanges {

	private reglementService = inject(ReglementService);
	private spin = inject(SpinnerService);
	private destroyRef = inject(DestroyRef);
	private overlay = inject(Overlay);
	private vcr = inject(ViewContainerRef);
	private fc = new FormatCurrencyPipe();

	// Magasin de la barre de l'onglet (null = Tous), seul filtre serveur partagé ; la caisse se choisit dans la carte.
	@Input() store: number | null = null;
	// Jour affiché par l'onglet (AAAA-MM-JJ) : la première ouverture propose son mois, jusqu'à ce jour.
	@Input() day: string | null = null;
	@Input() stores: Store[] = [];
	@Input() stations: Station[] = [];

	@ViewChild('peekPanel') private peekTpl?: TemplateRef<unknown>;

	// Fermée à l'arrivée : la lecture part à l'ouverture, jamais avant.
	public open = false;
	public from: Date | null = null;
	public to: Date | null = null;
	public person: number | null = null;
	// Caisse filtrée côté navigateur (null = Toutes), comme la personne : aucune requête.
	public station: number | null = null;
	// Comptages justes masqués par défaut ; les écarts incalculables restent, ils peuvent cacher un manque.
	public gapsOnly = true;
	public data: CashGaps | null = null;
	public error = '';
	public loading = false;
	public people: GapPerson[] = [];
	public stationOpts: StationOption[] = [];
	public nights: NightRow[] = [];
	public days: DayRow[] = [];
	public envelopes: EnvelopeRow[] = [];
	public envelopesTotal = 0;
	public strongboxCounts: StrongboxCountRow[] = [];
	public strongboxCountTotals: GapTotals = gapTotals([]);
	public nightTotals: GapTotals | null = null;
	public dayTotals: GapTotals | null = null;
	public peek: Peek | null = null;
	readonly peekId = PEEK_ID;
	// Nom de la personne filtrée, gardé si une relecture ne la cite plus ; idem pour la caisse.
	private personName: string | null = null;
	private stationPick: StationOption | null = null;
	// Lignes de la lecture affichée, bâties une fois par lecture : un filtre ne fait que les trier (les fenêtres se bâtissent au survol).
	private allNights: NightRow[] = [];
	private allDays: DayRow[] = [];
	private allEnvelopes: EnvelopeRow[] = [];
	private allStrongboxCounts: StrongboxCountRow[] = [];
	private peekRef: OverlayRef | null = null;
	private peekAnchor: HTMLElement | null = null;
	private loadToken = 0;
	// Filtres serveur de la lecture affichée (ou en cours) : rouvrir sans les changer ne relit rien.
	private readKey: string | null = null;

	readonly hints = GAP_HINTS;
	readonly reservedHint = SAFE_RESERVED_HINT;
	readonly peekColumns = PEEK_COLUMNS;
	readonly personLabel = personLabel;
	readonly nameOf = nameOf;
	readonly stationLabel = stationLabel;
	readonly storeLabel = storeLabel;
	readonly strongboxLabel = strongboxLabel;
	readonly signed = signedMoney;

	constructor() {
		this.destroyRef.onDestroy(() => this.hidePeek());
		// Mouvement (dépôt au coffre compris) ou inventaire fait depuis l'onglet : carte ouverte relue sans voile (la boîte affiche son succès), repliée relue à sa prochaine ouverture.
		merge(this.reglementService.changed$, this.reglementService.strongboxCountsChanged$).pipe(takeUntilDestroyed()).subscribe(() => {
			this.readKey = null;
			if (this.open) this.load(true);
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Magasin qui ne contient plus la caisse choisie : retour à « Toutes » plutôt qu'une liste vide inexpliquée.
		if (changes['store'] && this.station !== null && this.store !== null && this.stationPick?.store !== this.store) {
			this.station = null;
			this.stationPick = null;
			this.refilter();
		}
		if (changes['store'] || changes['stations'] || changes['stores']) this.refreshOptions();
		if (changes['store'] && this.open && this.key() !== this.readKey) this.load();
	}

	toggle(): void {
		this.open = !this.open;
		if (!this.open) { this.hidePeek(); return; }
		if (!this.from || !this.to) {
			const to = this.day ?? todayStr();
			this.setWindow(dayjs(to).startOf('month').format(ISO_DATE), to);
		}
		if (this.key() !== this.readKey) this.load();
	}

	private key(): string {
		return JSON.stringify([pickedDateStr(this.from), pickedDateStr(this.to), this.store]);
	}

	private setWindow(from: string, to: string): void {
		this.from = dayjs(from).toDate();
		this.to = dayjs(to).toDate();
	}

	load(quiet = false): void {
		const key = this.key();
		const token = ++this.loadToken;
		this.readKey = key;
		this.loading = true;
		const read$ = this.reglementService.getCashGaps$({ from: pickedDateStr(this.from), to: pickedDateStr(this.to), store: this.store });
		(quiet ? read$ : read$.pipe(this.spin.block('Chargement des écarts de caisse…'))).pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (g) => {
					if (token !== this.loadToken) return;
					this.loading = false;
					this.data = g;
					this.error = '';
					this.allNights = g.nights.map(nightRow);
					this.allDays = g.days.map(dayRow);
					this.allEnvelopes = (g.envelopes ?? []).map(envelopeRow);
					this.allStrongboxCounts = g.strongbox_counts.map(strongboxCountRow);
					this.people = gapPeople(g);
					this.refreshOptions();
					this.refilter();
				},
				error: (e) => {
					if (token !== this.loadToken) return;
					this.loading = false;
					this.readKey = null;
					this.error = refusalText(e);
					// Relecture discrète en échec : les lignes d'avant restent sous le message ; une lecture demandée vide la carte, listes Caisse et Personne comprises.
					if (quiet && this.data) return;
					this.data = null;
					this.allNights = [];
					this.allDays = [];
					this.allEnvelopes = [];
					this.allStrongboxCounts = [];
					this.people = [];
					this.refreshOptions();
					this.refilter();
				},
			});
	}

	private refreshOptions(): void {
		const opts = stationOptions(this.stations, this.stores, this.store, this.data);
		const s = this.station;
		this.stationOpts = s === null || opts.some((o) => o.id === s) ? opts : [...opts, this.stationPick ?? { id: s, store: null, name: stationLabel(s, null), label: stationLabel(s, null) }];
	}

	// Personne absente de la nouvelle lecture : gardée, la liste vide le dit mieux qu'un filtre retiré en silence.
	private refilter(): void {
		this.hidePeek();
		const p = this.person, s = this.station;
		const nights = this.allNights.filter((n) => s === null || n.station === s);
		const days = this.allDays.filter((d) => s === null || d.station === s);
		const mine = <T extends { status: GapStatus }>(rows: T[], who: (r: T) => number[]) => rows.filter((r) => p === null || who(r).includes(p));
		this.nights = mine(nights, nightPeople).filter((n) => !this.gapsOnly || n.status !== 'ok');
		this.days = mine(days, dayPeople).filter((d) => !this.gapsOnly || d.status !== 'ok');
		this.nightTotals = this.data ? gapTotals(mine(nights, nightPeople)) : null;
		this.dayTotals = this.data ? gapTotals(mine(days, dayPeople)) : null;
		this.envelopes = this.allEnvelopes.filter((e) => (s === null || e.source === s) && (p === null || e.id_user === p));
		this.envelopesTotal = sumCents(this.envelopes.map((e) => e.amount));
		// Inventaires : la personne et « Écarts seulement » filtrent, pas la caisse (un coffre n'est pas une caisse) ; le pied compte aussi les justes.
		const counts = this.allStrongboxCounts.filter((c) => p === null || c.id_user === p);
		this.strongboxCounts = counts.filter((c) => !this.gapsOnly || c.status !== 'ok');
		this.strongboxCountTotals = gapTotals(counts);
		if (p !== null && !this.people.some((x) => x.id === p)) this.people = [...this.people, { id: p, name: this.personName ?? null }];
	}

	// Début : une fin antérieure le suit ; fin : un début postérieur la suit (règle de la barre de l'onglet).
	fromChange(d: any): void {
		if (!pickedDateStr(d)) { this.from = this.from ? new Date(this.from) : null; return; }
		this.from = d;
		if ((pickedDateStr(this.to) ?? '') < (pickedDateStr(d) ?? '')) this.to = d;
		this.load();
	}

	toChange(d: any): void {
		if (!pickedDateStr(d)) { this.to = this.to ? new Date(this.to) : null; return; }
		this.to = d;
		if ((pickedDateStr(this.from) ?? '') > (pickedDateStr(d) ?? '')) this.from = d;
		this.load();
	}

	thisMonth(): void {
		const today = todayStr();
		this.setWindow(dayjs(today).startOf('month').format(ISO_DATE), today);
		this.load();
	}

	lastMonth(): void {
		const m = dayjs(todayStr()).subtract(1, 'month');
		this.setWindow(m.startOf('month').format(ISO_DATE), m.endOf('month').format(ISO_DATE));
		this.load();
	}

	lastWeek(): void {
		const today = todayStr();
		this.setWindow(dayjs(today).subtract(6, 'day').format(ISO_DATE), today);
		this.load();
	}

	setPerson(id: number | null): void {
		this.person = id;
		this.personName = this.people.find((x) => x.id === id)?.name ?? null;
		this.refilter();
	}

	setStation(id: number | null): void {
		this.station = id;
		this.stationPick = this.stationOpts.find((o) => o.id === id) ?? null;
		this.refilter();
	}

	setGapsOnly(on: boolean): void {
		this.gapsOnly = on;
		this.refilter();
	}

	private fileScope(g: CashGaps): [{ id: number; name?: string } | null, { id: number; name: string } | null] {
		const store = g.store === null ? null : { id: g.store, name: this.stores.find((s) => s.id === g.store)?.name };
		const station = this.station === null ? null : { id: this.station, name: this.stationPick?.name ?? stationLabel(this.station, null) };
		return [store, station];
	}

	exportCsv(): void {
		const g = this.data;
		if (!g || !this.canExport) return;
		downloadCsv(csvFileName('ecarts-de-caisse', g.date, g.date_to, ...this.fileScope(g)), cashGapsCsv(this.nights, this.days));
	}

	exportEnvelopesCsv(): void {
		const g = this.data;
		if (!g || !this.envelopes.length) return;
		downloadCsv(csvFileName('enveloppes-coffre', g.date, g.date_to, ...this.fileScope(g)), envelopesCsv(this.envelopes));
	}

	// Ni le filtre Caisse ni son suffixe : il ne s'applique pas aux coffres.
	exportStrongboxCountsCsv(): void {
		const g = this.data;
		if (!g || !this.strongboxCounts.length) return;
		downloadCsv(csvFileName('inventaires-coffres', g.date, g.date_to, this.fileScope(g)[0]), strongboxCountsCsv(this.strongboxCounts));
	}

	get canExport(): boolean {
		return !!this.data && (this.nights.length > 0 || this.days.length > 0);
	}

	// Magasin lu par la carte (réglé dans la barre de l'onglet), puis la caisse filtrée dans la carte.
	get scopeLabel(): string {
		const store = this.store === null ? 'tous les magasins' : (this.stores.find((s) => s.id === this.store)?.name ?? 'magasin ' + this.store);
		return this.station === null ? store : `${store}, ${this.stationPick?.name ?? stationLabel(this.station, null)}`;
	}

	get windowLabel(): string {
		const g = this.data;
		return g ? `du ${dayLabel(g.date)} au ${dayLabel(g.date_to)}` : '';
	}

	// Hors des sommes : une nuit sans fond compté à la clôture est « incalculable », un Z sans fond compté à l'ouverture « non significatif » ; un inventaire n'en a jamais.
	totalsLabel(t: GapTotals, unit: 'comptage' | 'inventaire', uncertain?: 'incalculable' | 'non significatif'): string {
		const parts = [`${t.gaps} écart${t.gaps > 1 ? 's' : ''} sur ${t.counts} ${unit}${t.counts > 1 ? 's' : ''}`];
		if (t.uncertain && uncertain) parts.push(`${t.uncertain} ${uncertain}${t.uncertain > 1 ? 's' : ''}`);
		return parts.join(', ');
	}

	driftHint(d: DayRow): string {
		return `Attendu figé au Z : ${this.fc.transform(d.expected)} €. Le détail recalculé aujourd'hui donne ${this.fc.transform(d.recomputed)} € (donnée modifiée après le Z)`;
	}

	peekLabel(p: LazyPeek): string {
		return `${p.title} : ${this.fc.transform(p.total)} €, ${p.note}, ${p.context}`;
	}

	// Panneau CDK hors de la carte : son overflow: clip rognerait un panneau positionné en CSS ; une infobulle texte n'aligne pas les montants.
	// Collé au montant (décalage 0) : la souris passe de l'un à l'autre sans quitter les deux, la liste longue se fait défiler.
	showPeek(ev: Event, p: LazyPeek | null): void {
		const anchor = ev.currentTarget as HTMLElement | null;
		if (!p || !anchor || !this.peekTpl || (this.peekRef && this.peekAnchor === anchor)) return;
		this.hidePeek();
		this.peek = p.open();
		this.peekAnchor = anchor;
		this.peekRef = this.overlay.create({
			positionStrategy: this.overlay.position().flexibleConnectedTo(anchor)
				.withPositions([
					{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
					{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
					{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
					{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
				])
				.withPush(true)
				.withViewportMargin(8),
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
		});
		this.peekRef.attach(new TemplatePortal(this.peekTpl, this.vcr));
		// Échap au niveau du document, focus où qu'il soit : ouverte à la souris, le montant n'a pas le focus. L'abonnement meurt avec dispose().
		this.peekRef.keydownEvents().pipe(filter((e) => e.key === 'Escape')).subscribe(() => this.hidePeek());
		anchor.setAttribute('aria-describedby', PEEK_ID);
	}

	peekKey(ev: KeyboardEvent): void {
		if (ev.key === 'Escape') { this.hidePeek(); return; }
		const to = PEEK_SCROLL[ev.key];
		const body = this.peekRef?.overlayElement.querySelector<HTMLElement>('.peek-scroll');
		if (!to || !body) return;
		ev.preventDefault();
		body.scrollTop = to(body);
	}

	// Focus parti du montant ou de la fenêtre : fermée, sauf s'il passe de l'un à l'autre (clic dans la fenêtre) ou si la souris y est (clic sur sa barre de défilement).
	blurPeek(ev: FocusEvent): void {
		const to = ev.relatedTarget as Node | null;
		const panel = this.peekRef?.overlayElement;
		if ((to && (panel?.contains(to) || this.peekAnchor?.contains(to))) || panel?.matches(':hover')) return;
		this.hidePeek();
	}

	// Souris sortie du montant ou de la fenêtre : fermée, sauf si elle passe de l'un à l'autre.
	leavePeek(ev: MouseEvent): void {
		const to = ev.relatedTarget as Node | null;
		if (to && (this.peekRef?.overlayElement.contains(to) || this.peekAnchor?.contains(to))) return;
		this.hidePeek();
	}

	// Champs remis à zéro avant dispose() : un focusout levé par le retrait du volet retrouve une fenêtre déjà fermée.
	// Focus dans le volet (clic dans la liste) : rendu au montant avant le retrait, sinon il tomberait sur <body> ; showPeek voit l'ancre déjà ouverte.
	hidePeek(): void {
		const ref = this.peekRef;
		if (ref?.overlayElement.contains(document.activeElement)) this.peekAnchor?.focus({ preventScroll: true });
		this.peekAnchor?.removeAttribute('aria-describedby');
		this.peekRef = null;
		this.peekAnchor = null;
		this.peek = null;
		ref?.dispose();
	}
}
